/**
 * eventCapabilities — what the viewer may do to a calendar event, and WHY not.
 *
 * The rules were scattered across three components (EventTagChip's drag rule,
 * CalendarDayView's copy of it, and EventDetailModal's per-kind button rows).
 * They are lifted here verbatim, in one place, and each withheld action now
 * carries a reason the UI shows — #680: "a read-only event explains WHY".
 *
 * Deliberate, and not to be loosened:
 *   - A `booking_link`-sourced chip has no provider event to move. Moving it
 *     would move the CRM record with no invite to the guest, so it can only be
 *     cancelled. (A booking's provider TWIN — same booking_id, source google or
 *     outlook — can be edited: that route moves the real event and syncs the
 *     booking.) This is the old `canEditLinkBooking`.
 *   - Recurring instances are never DRAGGED (an Outlook series instance moved by
 *     drag is too easy to do by accident); they can be edited deliberately in
 *     the editor, which changes only that occurrence (Graph and Google both
 *     scope an instance id to the instance).
 *   - Holidays / focus time are the provider's; nothing here edits them.
 *   - Tagging (#680) is NOT editing: a tag is a CRM-side `taggables` row
 *     (#682) that never reaches the provider, so `canTag` is true for every
 *     event the viewer sees (always their own), including a booking_link chip
 *     and a holiday — without loosening `canEdit` / `canDrag` for them.
 */
import { SERIES_TAG_NOTE } from '../tagCopy';

export const SOURCE_LABELS = {
    google: 'Google Calendar',
    outlook: 'Outlook',
    coach_booking: 'CRM Booking',
    booking_link: 'CRM Booking Link',
    crm: 'NurtureCRM',
};

export const KIND_LABELS = {
    meeting: 'Meeting',
    booking: 'Booking',
    holiday: 'Holiday',
    out_of_office: 'Out of office',
};

/** Which provider holds this event, for "edit it in …" copy. */
export function providerLabel(event) {
    if (event?.source === 'outlook') return 'Outlook';
    if (event?.source === 'google') return 'Google Calendar';
    return 'your calendar provider';
}

/**
 * @param {object} event - a CalendarEvent from the aggregated endpoint
 * @returns {{
 *   canEdit: boolean, canDrag: boolean, canCancelBooking: boolean, canDelete: boolean,
 *   readOnlyReason: string|null, note: string|null, canTag: boolean, tagNote: string|null
 * }}
 *   `readOnlyReason` explains a withheld edit; `note` is advisory (edit allowed);
 *   `tagNote` says how far a tag reaches (a recurring occurrence = its series).
 */
export function eventCapabilities(event) {
    const none = { canEdit: false, canDrag: false, canCancelBooking: false, canDelete: false, readOnlyReason: null, note: null, canTag: false, tagNote: null };
    if (!event) return none;
    const tagging = { canTag: true, tagNote: event.is_recurring_instance ? SERIES_TAG_NOTE : null };

    const isLinkChip = event.source === 'booking_link';
    const isRecurring = Boolean(event.is_recurring_instance);
    const kind = event.kind;

    if (kind === 'holiday') {
        return {
            ...none,
            ...tagging,
            readOnlyReason: `Holiday and focus-time blocks are managed in ${providerLabel(event)}.`,
        };
    }

    const isBooking = kind === 'booking';
    // Old EventDetailModal: bookings are cancelled, meetings and OOO deleted.
    const canCancelBooking = isBooking;
    const canDelete = kind === 'meeting' || kind === 'out_of_office';
    // Old canEditLinkBooking (`isLinkBooking && source !== 'booking_link'`): a
    // link booking is editable only through its provider twin, never the chip.
    const canEdit = !isLinkChip;
    // Old EventTagChip rule, verbatim.
    const canDrag = (kind === 'meeting' || isBooking) && !isRecurring && !isLinkChip;

    let readOnlyReason = null;
    if (isLinkChip) {
        readOnlyReason = 'This booking has no calendar event — no calendar was connected when the guest '
            + 'booked, or the calendar write failed — so it is shown from the CRM record and can only be '
            + 'cancelled, not rescheduled.';
    }
    const note = isRecurring
        ? `One occurrence of a recurring series. Changes here apply to this occurrence only; edit the whole series in ${providerLabel(event)}.`
        : null;

    return { canEdit, canDrag, canCancelBooking, canDelete, readOnlyReason, note, ...tagging };
}

/** Consequence-bearing confirmation copy for the destructive action (#680). */
export function destructiveCopy(event) {
    const caps = eventCapabilities(event);
    if (caps.canCancelBooking) {
        return {
            title: 'Cancel this booking?',
            body: 'The guest will be notified by email that the booking is cancelled. This cannot be undone.',
            confirm: 'Cancel booking',
            done: 'Booking cancelled',
            failed: 'Failed to cancel booking',
        };
    }
    const where = event?.source === 'crm' ? 'NurtureCRM and your connected calendar' : (SOURCE_LABELS[event?.source] || 'your calendar');
    return {
        title: 'Delete this event?',
        body: `It will be removed from ${where}. This cannot be undone.`,
        confirm: 'Delete event',
        done: 'Event deleted',
        failed: 'Failed to delete event',
    };
}
